import * as React from 'react'
import { ScrollView, Text, TouchableOpacity, View, Image } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Icon from '@components/icon/Icons'
import SmallButton from '@components/button/SmallButton'
import Animators from '@components/root/Animators'
import { Viewer } from '@components/plan/ViewerComponent'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as LocationStore from '@store/location/location'
import * as FamilleStore from '@store/famille/famille'
import * as SocieteStore from '@store/societe/societe'
import * as SiteStore from '@store/site/site'
import * as UserStore from '@store/user/user'
import * as AuthStore from '@store/auth/auth'
import * as PlanStore from '@store/plan/plan'

import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'

import { AuthScope } from '@api/enum'

const Recap = ({ form, onForm }: IncidentFormProps) => {
  const i18n = useI18n()
  const [, bp] = useBreakpoint()
  const navigation = useNavigation()

  const places = useReducer(LocationStore.store, (s) => s.places)
  const activities = useReducer(FamilleStore.store, (s) => s.activities)
  const services = useReducer(SocieteStore.store, (s) => s.services)
  const sites = useReducer(SiteStore.store, (s) => s.sites)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations.inProgress)
  const user = useReducer(UserStore.store, (s) => s.user)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const plans = useReducer(PlanStore.store, (s) => s.plans)

  const currentPlan = React.useMemo(() => {
    const current = plans
      .filter((plan) => !!plan.viewers.geojson && !!plan.viewers.geojson.path)
      .find((plan) => plan.localisationId === form.space)
    if (!!current && !!current.viewers.geojson) {
      return current.viewers.geojson
    }
    return undefined
  }, [form.space])

  const place = places.find((p) => p.id === form.space)
  const location = places.find((p) => p.id === form.location)
  const subLoc = places.find((p) => p.id === form.subLocation)
  const acts = activities
    .filter((ac) => (form.familles || []).find((a) => a === ac.id))
    .filter(Boolean) as FamilleResponse[]
  const service = services.find((s) => s.id === form.societePrestataireId)
  const site = sites.find((s) => s.id === form.siteId)
  const manifestation = manifestations.find((m) => m.id === form.manifestationId)

  const displayCheckPiority = scopes.includes(AuthScope.DemandesPrioritaireCreation)
  // TODO : utiliser les scopes EDL
  const displayCheckEDL = !!user && user.typeUtilisateur === 'VIPARIS' && form.isInfrastructure === 'INFRA'

  React.useEffect(() => {
    onForm({ completed: true })
  }, [])

  const renderContent = () => {
    return (
      <View>
        <Animators>
          <View>
            <TitleContainer>
              <Icon name="location" color={theme.colors.primary} size={24} />
              <Title>{i18n.t('screens.incident.RECAP.location')}</Title>
              <SmallButton
                label={i18n.t('common.modify')}
                onClick={() => navigation.push(`/incident/${!!user ? 'manif' : 'manif_expo'}?mode=edit`)}
                status="active"
                color="light"
              />
            </TitleContainer>
            {!!site && <Info>{i18n.t(`screens.site.sites.${site.codeLieu}`)}</Info>}
            {!!manifestation && <Info>{manifestation.libelle}</Info>}
            {!!form.enseigneCommande && <Info>{form.enseigneCommande}</Info>}

            {/* location */}
            {!!place && <Info>{place.libelle}</Info>}
            {!!location && <Info>{`${location.libelle}${!!subLoc ? ` - ${subLoc.libelle}` : ''}`}</Info>}

            {/* enseigne */}
            {!!form.standCommande && (
              <Info>{`${form.espaceCommande} - ${form.alleeCommande} - ${form.standCommande}`}</Info>
            )}

            {/* plan*/}
            {currentPlan && !!form.plan && !!form.plan.x && !!form.plan.y && (
              <Viewer
                planUrl={currentPlan.path}
                pins={[
                  { id: 'id', x: parseFloat(form.plan.x), y: parseFloat(form.plan.y), color: theme.colors.primary },
                ]}
                initialPosition={{ center: [parseFloat(form.plan.x), parseFloat(form.plan.y)], zoom: 17 }}
                height={160}
              />
            )}
          </View>

          {/* type */}
          <View>
            <TitleContainer>
              <Icon name="hammer" color={theme.colors.primary} size={24} />
              <Title>{i18n.t('screens.incident.RECAP.action')}</Title>
              <SmallButton
                label={i18n.t('common.modify')}
                onClick={() => navigation.push(`/incident/${!!user ? 'type' : 'command_expo'}?mode=edit`)}
                status="active"
                color="light"
              />
            </TitleContainer>
            <Info>
              {i18n.t(`screens.incident.RECAP.${form.isInfrastructure === 'INFRA' ? 'infrastructure' : 'prestation'}`)}
            </Info>

            {acts.length > 0 && (
              <Info>{`${acts.map((a) => a.libelle).join('\n')}\n${i18n.t('screens.incident.RECAP.quantity', {
                count: form.quantite,
              })}`}</Info>
            )}
          </View>

          {/* commentaire */}
          <View>
            <TitleContainer>
              <Icon name="comment" color={theme.colors.primary} size={24} />
              <Title>{i18n.t('screens.incident.RECAP.comment')}</Title>
              <SmallButton
                label={i18n.t('common.modify')}
                onClick={() => navigation.push('/incident/detail?mode=edit')}
                status="active"
                color="light"
              />
            </TitleContainer>
            <Comment>{form.commentaire || i18n.t('screens.incident.DETAIL.noComment')}</Comment>

            {!!form.photos && (
              <Photos>
                {form.photos.map((photo, i) => (
                  <Photo key={photo.filename} source={{ uri: photo.image }} />
                ))}
              </Photos>
            )}
          </View>

          {/* prestataire */}
          {service && (
            <View>
              <TitleContainer>
                <Icon name="prestataire" color={theme.colors.primary} size={24} />
                <Title>{i18n.t('screens.incident.RECAP.service')}</Title>
                <SmallButton
                  label={i18n.t('common.modify')}
                  onClick={() => navigation.push('/incident/presta?mode=edit')}
                  status="active"
                  color="light"
                />
              </TitleContainer>
              {!!service && <Info>{service.nameSociete}</Info>}
            </View>
          )}

          {/* statut */}
          {(displayCheckPiority || displayCheckEDL) && (
            <View>
              <TitleContainer>
                <Icon name="problem" color={theme.colors.primary} size={24} />
                <Title>{i18n.t('screens.incident.RECAP.status')}</Title>
                <SmallButton
                  label={i18n.t('common.modify')}
                  onClick={() => navigation.push('/incident/detail?mode=edit')}
                  status="active"
                  color="light"
                />
              </TitleContainer>
              <Info>{i18n.t(`screens.incident.RECAP.${form.isPrioritaire ? 'prioritaire' : 'nonPrioritaire'}`)}</Info>
              {displayCheckEDL && (
                <Info>{i18n.t(`screens.incident.RECAP.${form.edl ? 'isInEdl' : 'isNotInEdl'}`)}</Info>
              )}
            </View>
          )}

          {/* contact */}
          {form.nomContact && (
            <View>
              <TitleContainer>
                <Icon name="phone" color={theme.colors.primary} size={24} />
                <Title>{i18n.t('screens.incident.USER_DETAIL.contact')}</Title>
                <SmallButton
                  label={i18n.t('common.modify')}
                  onClick={() => navigation.push('/incident/user?mode=edit')}
                  status="active"
                  color="light"
                />
              </TitleContainer>
              <Info>{`${form.prenomContact} ${form.nomContact}`}</Info>
              <Info>{form.emailContact}</Info>
              <Info>{form.telephoneContact}</Info>
            </View>
          )}
        </Animators>
      </View>
    )
  }

  if (bp === 'phone') {
    return (
      <ScrollView>
        <Header>
          <Quit activeOpacity={0.9} onPress={() => navigation.push('/incident/start')}>
            <Icon name="close" size={25} color={theme.colors.background} />
          </Quit>
        </Header>
        <TitleView>
          <HeaderTitle>{i18n.t('screens.incident.RECAP.title')}</HeaderTitle>
        </TitleView>

        <Card>{renderContent()}</Card>
      </ScrollView>
    )
  }

  return <MainContainer>{renderContent()}</MainContainer>
}

export default Recap

const MainContainer = styled(View)`
  margin: 0 20px;
  padding-top: ${(props) => (props.theme.breakpoint === 'phone' ? 0 : 25)}px;
`

const TitleContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
  margin-top: 15px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin: 0 10px;
  flex: 1;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 10px;
`

const Comment = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  padding: 12px 16px;
  margin-bottom: 10px;
`

const Photos = styled(View)`
  flex-direction: row;
`

const Photo = styled(Image)`
  height: 70px;
  width: 70px;
  margin-right: 8px;
`

// phone

const Header = styled(View)`
  padding-top: 16px;
  padding-left: 16px;
  background-color: ${(props) => props.theme.colors.primary};
`

const TitleView = styled(View)`
  background-color: ${(props) => props.theme.colors.primary};
  padding: 16px;
  padding-bottom: 40px;
`

const Quit = styled(TouchableOpacity)`
  padding: 5px 0;
  margin-bottom: 24px;
  align-self: flex-start;
`

const HeaderTitle = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  color: ${(props) => props.theme.colors.background};
  font-size: 24px;
`

const Card = styled(View)`
  margin: 0 16px;
  padding: 0 16px;
  padding-bottom: 16px;
  margin-bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 0 4px rgb(47 38 127 / 10%);
  background-color: ${(props) => props.theme.colors.background};
  top: -24px;
`
